import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  completeOnboarding,
  getOnboardingContent,
  getOnboardingVideoBlob
} from '../services/onboardingService';
import { getCreatorOnboardingToken } from '../services/sessionStorage';

function isValidWallet(value) {
  return /^0x[a-fA-F0-9]{40}$/.test(String(value || '').trim());
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { creator, onboardingToken, loading, login, clearOnboarding, logout } = useAuth();
  const [content, setContent] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [selectedCreatorType, setSelectedCreatorType] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const activeOnboardingToken = onboardingToken || getCreatorOnboardingToken();

  const profile = content?.profile;
  const creatorUsername = profile?.username ? `@${profile.username}` : '@creator';
  const trackContent = selectedCreatorType && content?.creatorTracks?.[selectedCreatorType]
    ? content.creatorTracks[selectedCreatorType]
    : content;
  const walletReady = useMemo(() => isValidWallet(walletAddress), [walletAddress]);
  const contentSections = trackContent?.sections || [];
  const steps = [
    {
      label: 'Intro',
      kind: 'intro',
      title: trackContent?.welcome?.title || 'Hey Creator, welcome aboard!',
      copy: trackContent?.welcome?.body || 'We\'re really excited to have you join our creator community. Here\'s a quick overview of how things work with us so you know what to expect.'
    },
    ...contentSections.map((section) => ({
      label: section.navLabel || section.title,
      kind: 'content',
      title: section.title,
      copy: section.body,
      items: section.items || []
    })),
    {
      label: 'Payout',
      kind: 'payout',
      title: trackContent?.form?.title || 'Complete Your Creator Profile',
      copy: trackContent?.form?.body || 'Your X account is already connected. Add the Ethereum wallet where you want to receive campaign payments.'
    }
  ];
  const isLastStep = activeStep === steps.length - 1;
  const currentStep = steps[activeStep];

  const nextStep = () => {
    setError(null);
    setActiveStep((step) => Math.min(step + 1, steps.length - 1));
  };

  const selectCreatorType = (creatorType) => {
    setSelectedCreatorType(creatorType);
    setActiveStep(0);
    setError(null);
  };

  const previousStep = () => {
    setError(null);
    setActiveStep((step) => Math.max(step - 1, 0));
  };

  useEffect(() => {
    if (!activeOnboardingToken || loading) return undefined;

    let cancelled = false;
    let objectUrl = null;

    getOnboardingContent()
      .then(async (response) => {
        if (cancelled) return;
        const data = response.data?.data;
        setContent(data);
        setError(null);

        if (data?.videoUrl) {
          try {
            objectUrl = await getOnboardingVideoBlob(data.videoUrl);
            if (!cancelled) setVideoSrc(objectUrl);
          } catch {
            if (!cancelled) setVideoSrc(null);
          }
        }
      })
      .catch((loadError) => {
        if (cancelled) return;
        const code = loadError.response?.data?.code;
        setError(code === 'ONBOARDING_SESSION_EXPIRED'
          ? 'Your onboarding session expired. Please sign in with X again.'
          : 'We could not load onboarding right now. Please retry in a moment.');
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [loading, activeOnboardingToken]);

  const submit = async (event) => {
    event.preventDefault();
    if (!walletReady || submitting || !selectedCreatorType) return;

    setSubmitting(true);
    setError(null);
    try {
      const response = await completeOnboarding(walletAddress.trim(), selectedCreatorType);
      await login(response.data.token);
      clearOnboarding();
      navigate('/dashboard', { replace: true });
    } catch (submitError) {
      setError(submitError.response?.data?.error || 'Could not complete onboarding. Please check your wallet and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-state" style={{ minHeight: '100vh' }}>
        Loading...
      </div>
    );
  }

  if (creator) return <Navigate to="/dashboard" replace />;
  if (!activeOnboardingToken) return <Navigate to="/login" replace />;

  return (
    <div className="onboarding-screen">
      <div className="onboarding-watermark">TAKEOVER TAKEOVER TAKEOVER</div>
      <header className="onboarding-top">
        <button className="onboarding-brand" onClick={() => navigate('/login')}>
          TAKEOVER
        </button>
        {profile && (
          <div className="onboarding-account">
            <button
              className="onboarding-account-trigger"
              type="button"
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              onClick={() => setAccountMenuOpen((open) => !open)}
            >
              <span className="onboarding-avatar round">
                {profile.profileImageUrl ? (
                  <img src={profile.profileImageUrl} alt="" />
                ) : (
                  <span>{profile.username?.[0] || 'C'}</span>
                )}
              </span>
              <span className="onboarding-handle">@{profile.username}</span>
            </button>
            {accountMenuOpen && (
              <div className="onboarding-account-menu" role="menu">
                <button type="button" role="menuitem" onClick={logout}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="onboarding-main">
        {!selectedCreatorType ? (
          <section className="onboarding-selector" aria-label="Choose creator track">
            <div className="onboarding-selector-bg" />
            <div className="onboarding-selector-copy">
              <div className="sec-eyebrow">Creator access confirmed</div>
              <h1 className="onboarding-selector-title">
                Welcome to <span className="onboarding-selector-brand">TAKEOVER</span>, <span className="onboarding-selector-username">{creatorUsername}</span>
              </h1>
              <p className="onboarding-selector-sub">
                Choose the creator track that matches your work. We will tune the setup and campaign flow around it.
              </p>
            </div>

            <div className="onboarding-track-grid">
              <button className="onboarding-track-card" type="button" onClick={() => selectCreatorType('ai')}>
                <span className="onboarding-track-kicker">Track 01</span>
                <strong>AI Creator</strong>
                <span>For builders, educators, analysts, and creators making content around AI tools, workflows, and products.</span>
              </button>
              <button className="onboarding-track-card featured" type="button" onClick={() => selectCreatorType('web3')}>
                <span className="onboarding-track-kicker">Track 02</span>
                <strong>Web3 Creator</strong>
                <span>For crypto-native creators joining brand campaigns, launch support, QRTs, threads, and Web3 activations.</span>
              </button>
            </div>
          </section>
        ) : (
          <>
            <section className="onboarding-hero">
              <div>
                <div className="sec-eyebrow">{selectedCreatorType === 'ai' ? 'AI creator onboarding' : 'Web3 creator onboarding'}</div>
                <h1 className="onboarding-title">Creator setup</h1>
                <p className="onboarding-copy">
                  Your X account is verified. Finish the essentials one step at a time, then enter the portal.
                </p>
              </div>
              <button className="onboarding-track-switch" type="button" onClick={() => setSelectedCreatorType(null)}>
                Change track
              </button>
            </section>

            <section className="onboarding-stage">
          <nav className="onboarding-progress" aria-label="Onboarding progress">
            {steps.map((step, index) => (
              <button
                key={step.label}
                className={`onboarding-progress-step ${index === activeStep ? 'active' : ''} ${index < activeStep ? 'done' : ''}`}
                type="button"
                onClick={() => {
                  setError(null);
                  setActiveStep(index);
                }}
              >
                <span className="onboarding-progress-number">{String(index + 1).padStart(2, '0')}</span>
                <span className="onboarding-progress-label">{step.label}</span>
              </button>
            ))}
          </nav>

          <article className={`onboarding-focus-card ${currentStep.kind === 'content' ? 'content-step' : ''}`}>
            <div className="onboarding-focus-copyblock">
              <div className="onboarding-focus-meta">
                <span>Step {String(activeStep + 1).padStart(2, '0')} / {String(steps.length).padStart(2, '0')}</span>
                <span>{currentStep.label}</span>
              </div>
              <h2 className="onboarding-focus-title">{currentStep.title}</h2>
              {currentStep.kind !== 'content' && (
                <p className="onboarding-focus-copy">{currentStep.copy}</p>
              )}
            </div>

            {currentStep.kind === 'intro' && (
              <div className="onboarding-focus-video">
                {videoSrc ? (
                  <video className="onboarding-video" controls playsInline src={videoSrc} />
                ) : (
                  <div className="onboarding-video-fallback">
                    <span>Intro video loading</span>
                  </div>
                )}
              </div>
            )}

            {currentStep.kind === 'content' && (
              <div className="onboarding-brief-list">
                {(currentStep.items.length ? currentStep.items : [currentStep.copy]).map((item, index) => (
                  <div className="onboarding-brief-item" key={`${currentStep.label}-${item}`}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            )}

            {currentStep.kind === 'payout' && (
              <form className="onboarding-wallet-form" onSubmit={submit}>
                <label className="settings-label" htmlFor="walletAddress">
                  {trackContent?.form?.walletLabel || 'Wallet Address'}
                </label>
                <input
                  id="walletAddress"
                  className="settings-input"
                  value={walletAddress}
                  onChange={(event) => setWalletAddress(event.target.value)}
                  placeholder="0x..."
                  autoComplete="off"
                  spellCheck="false"
                />
                <div className="onboarding-wallet-note">
                  {trackContent?.form?.walletHelp || 'Your Ethereum wallet address (0x...)\nThis is where we\'ll send your USDC payments. Make sure it\'s on Ethereum mainnet!'}
                </div>
                {trackContent?.form?.footer && (
                  <div className="onboarding-secure-note">{trackContent.form.footer}</div>
                )}
                {error && <div className="onboarding-error">{error}</div>}
              </form>
            )}

            {error && currentStep.kind !== 'payout' && <div className="onboarding-error">{error}</div>}

            <div className="onboarding-step-actions">
              <button
                className="settings-btn secondary"
                type="button"
                onClick={previousStep}
                disabled={activeStep === 0 || submitting}
              >
                Back
              </button>
              {isLastStep ? (
                <button
                  className="submit-btn"
                  type="button"
                  disabled={!walletReady || submitting}
                  onClick={submit}
                >
                  {submitting ? 'Creating profile...' : 'Complete onboarding'}
                </button>
              ) : (
                <button className="submit-btn" type="button" onClick={nextStep}>
                  Continue
                </button>
              )}
            </div>
          </article>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
